import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, Package, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSlotAvailability } from "@/hooks/useSlotAvailability";

interface SchedulePickupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userAddress: string;
  approvedPackageCount: number;
  onSuccess: () => void;
  required?: boolean;
}

const TIME_SLOTS = [
  { value: "9:00 AM - 12:00 PM", label: "Morning (9 AM - 12 PM)" },
  { value: "12:00 PM - 3:00 PM", label: "Afternoon (12 PM - 3 PM)" },
  { value: "3:00 PM - 6:00 PM", label: "Evening (3 PM - 6 PM)" },
];

export const SchedulePickupDialog = ({
  open,
  onOpenChange,
  userId,
  userAddress,
  approvedPackageCount,
  onSuccess,
  required = false,
}: SchedulePickupDialogProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { availability, loading: checkingAvailability, checkDateAvailability, isSlotAvailable, getSlotStatus } = useSlotAvailability();

  // Check availability when date changes
  useEffect(() => {
    if (selectedDate) {
      checkDateAvailability(selectedDate);
      // Clear time slot if previously selected slot is now full
      if (selectedTimeSlot && !isSlotAvailable(selectedTimeSlot)) {
        setSelectedTimeSlot("");
      }
    }
  }, [selectedDate, checkDateAvailability]);

  // Reset selected time slot when it becomes unavailable
  useEffect(() => {
    if (selectedTimeSlot && selectedDate && !isSlotAvailable(selectedTimeSlot)) {
      setSelectedTimeSlot("");
    }
  }, [availability, selectedTimeSlot, selectedDate, isSlotAvailable]);

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTimeSlot) return;

    setLoading(true);
    try {
      const { error } = await supabase.from("user_pickups").insert({
        user_id: userId,
        proposed_date: format(selectedDate, "yyyy-MM-dd"),
        proposed_time_slot: selectedTimeSlot,
        address: userAddress,
        status: "awaiting_admin_confirmation",
      });

      if (error) throw error;

      toast.success("Pickup request submitted! Awaiting confirmation.");
      onOpenChange(false);
      onSuccess();
      
      // Reset form
      setSelectedDate(undefined);
      setSelectedTimeSlot("");
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error scheduling pickup:", error);
      }
      toast.error("Failed to schedule pickup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isValid = selectedDate && selectedTimeSlot;

  // Disable dates in the past and today (need at least 1 day notice)
  const disabledDays = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date < tomorrow;
  };

  return (
    <Dialog open={open} onOpenChange={required ? undefined : onOpenChange}>
      <DialogContent className={cn("sm:max-w-[450px]", required && "[&>button]:hidden")} onPointerDownOutside={required ? (e) => e.preventDefault() : undefined} onEscapeKeyDown={required ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            Schedule Pickup
          </DialogTitle>
          <DialogDescription>
            {required
              ? "Please select a pickup date and time to continue. This is required for your first package."
              : "Choose your preferred pickup date and time. We'll confirm availability."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Package count info */}
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {approvedPackageCount} approved package{approvedPackageCount !== 1 ? "s" : ""} ready for pickup
            </span>
          </div>

          {/* Date Selection */}
          <div className="space-y-2">
            <Label>Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Select a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Slot Selection */}
          <div className="space-y-2">
            <Label>Preferred Time Window</Label>
            {!selectedDate ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                Please select a date first to see available time slots
              </p>
            ) : checkingAvailability ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                Checking availability...
              </p>
            ) : (
              <Select 
                value={selectedTimeSlot} 
                onValueChange={setSelectedTimeSlot}
                disabled={!selectedDate}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a time window">
                    {selectedTimeSlot && (
                      <span className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {selectedTimeSlot}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => {
                    const slotStatus = getSlotStatus(slot.value);
                    const isFull = slotStatus?.isFull ?? false;
                    const spotsLeft = slotStatus?.available ?? 0;
                    
                    return (
                      <SelectItem 
                        key={slot.value} 
                        value={slot.value}
                        disabled={isFull}
                        className={cn(isFull && "opacity-50")}
                      >
                        <div className="flex items-center justify-between w-full gap-4">
                          <span>{slot.label}</span>
                          {isFull ? (
                            <span className="text-xs text-destructive font-medium flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Slot Full
                            </span>
                          ) : spotsLeft <= 3 && spotsLeft > 0 ? (
                            <span className="text-xs text-amber-600 font-medium">
                              {spotsLeft} spots left
                            </span>
                          ) : null}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Address display */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Pickup Address</Label>
            <p className="text-sm p-3 bg-muted/30 rounded-lg">
              {userAddress || "No address on file"}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {!required && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!isValid || loading || !userAddress}
          >
            {loading ? "Submitting..." : "Request Pickup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
